import React, { useState, useEffect, useRef } from "react";
import { Collapse, Modal, ListGroup } from "react-bootstrap";
import { createPortal } from "react-dom";

/** Below this width, Personal Settings opens in a full-screen sheet instead of inline (saves main page height). */
const SETTINGS_SHEET_MAX_PX = 991;

// Custom Dropdown Component for Mobile-Friendly Selection
const CustomDropdown = ({ label, value, options, onChange, placeholder = "Select..." }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    if (isOpen && !isMobile && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen, isMobile]);

  // Close dropdown on scroll or resize
  useEffect(() => {
    if (isOpen && !isMobile) {
      const handleScroll = () => setIsOpen(false);
      const handleResize = () => setIsOpen(false);
      
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, isMobile]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  if (isMobile) {
    // Mobile: Full-screen modal
    return (
      <>
        <button
          className="mobile-dropdown-trigger"
          onClick={() => setIsOpen(true)}
        >
          <span className="dropdown-text">{displayText}</span>
          <span className="dropdown-arrow">▼</span>
        </button>

        <Modal
          show={isOpen}
          onHide={() => setIsOpen(false)}
          centered
          size="sm"
          className="mobile-dropdown-modal"
        >
          <Modal.Header closeButton className="py-2">
            <Modal.Title className="fs-6">{label}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <ListGroup variant="flush">
              {options.map((option) => (
                <ListGroup.Item
                  key={option.value}
                  action
                  active={value === option.value}
                  onClick={() => handleSelect(option.value)}
                  className="mobile-option-item-compact"
                >
                  {option.label}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Modal.Body>
        </Modal>
      </>
    );
  } else {
    // Desktop: Custom dropdown with Portal
    return (
      <>
        <div className="custom-dropdown-wrapper">
          <button
            ref={buttonRef}
            className="custom-dropdown-button"
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="dropdown-text">{displayText}</span>
            <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
          </button>
        </div>

        {isOpen && createPortal(
          <>
            <div className="dropdown-overlay-portal" onClick={() => setIsOpen(false)} />
            <div 
              className="custom-dropdown-menu-portal"
              style={{
                position: 'absolute',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 99999
              }}
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  className={`dropdown-option ${value === option.value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
      </>
    );
  }
};

const FilterBar = ({
  region,
  setRegion,
  country,
  setCountry,
  ageCategory,
  setAgeCategory,
  selectedProvider,
  setSelectedProvider,
  regionOptions,
  countryOptions,
  ageCategoryOptions,
  providerOptions,
  sortOption,
  setSortOption,
  videoType,
  setVideoType
}) => {
  // Smart UX: Auto-expand Personal Settings for first-time users to guide them
  // Collapse for returning users who already know about the settings
  const checkIfFirstTimeUser = () => {
    const storedPrefs = localStorage.getItem("userPreferences");
    
    if (!storedPrefs) {
      return true; // No preferences = first time user
    }
    
    try {
      const prefs = JSON.parse(storedPrefs);
      
      // Check if preferences are just defaults (not user-chosen)
      const defaultPrefs = {
        region: 'US',
        selectedProvider: 'Any',
        ageCategory: 'Adults',
        sortOption: 'popularity.desc',
        videoType: 'movie'
      };
      
      // Compare each preference to defaults
      const hasDefaultValues = (
        prefs.region === defaultPrefs.region &&
        prefs.selectedProvider === defaultPrefs.selectedProvider &&
        prefs.ageCategory === defaultPrefs.ageCategory &&
        prefs.sortOption === defaultPrefs.sortOption &&
        prefs.videoType === defaultPrefs.videoType
      );
      
      // If all values match defaults, treat as first-time user
      return hasDefaultValues;
    } catch (e) {
      return true; // Invalid data = treat as first time
    }
  };
  
  const [showSettings, setShowSettings] = useState(() => {
    const firstTime = checkIfFirstTimeUser();
    if (typeof window !== "undefined" && window.innerWidth <= SETTINGS_SHEET_MAX_PX) {
      return false;
    }
    return firstTime;
  });

  const [useSettingsSheet, setUseSettingsSheet] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= SETTINGS_SHEET_MAX_PX : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${SETTINGS_SHEET_MAX_PX}px)`);
    const sync = () => setUseSettingsSheet(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Extended options for dropdowns
  const countryOptionsExtended = [
    { label: "🇺🇸 United States", value: "US" },
    { label: "🇬🇧 United Kingdom", value: "GB" },
    { label: "🇨🇦 Canada", value: "CA" },
    { label: "🇦🇺 Australia", value: "AU" },
    { label: "🇩🇪 Germany", value: "DE" },
    { label: "🇫🇷 France", value: "FR" },
    { label: "🇪🇸 Spain", value: "ES" },
    { label: "🇮🇹 Italy", value: "IT" },
    { label: "🇯🇵 Japan", value: "JP" },
    { label: "🇰🇷 South Korea", value: "KR" },
    { label: "🇧🇷 Brazil", value: "BR" },
    { label: "🇲🇽 Mexico", value: "MX" },
    { label: "🇮🇳 India", value: "IN" },
    { label: "🇳🇱 Netherlands", value: "NL" },
    { label: "🇸🇪 Sweden", value: "SE" },
    { label: "🇳🇴 Norway", value: "NO" },
    { label: "🇹🇷 Turkey", value: "TR" },
    { label: "🇵🇱 Poland", value: "PL" },
    { label: "🇵🇹 Portugal", value: "PT" },
    { label: "🇫🇮 Finland", value: "FI" },
    { label: "🇩🇰 Denmark", value: "DK" }
  ];

  const providerOptionsExtended = [
    // Global Services
    { label: "Netflix", value: "8" },
    { label: "Disney+", value: "337" },
    { label: "Amazon Prime Video", value: "9" },
    { label: "HBO Max", value: "1899" },
    { label: "Hulu", value: "15" },
    { label: "Apple TV+", value: "350" },
    { label: "Paramount+", value: "531" },
    { label: "Peacock", value: "386" },
    { label: "Crunchyroll", value: "283" },
    { label: "Discovery+", value: "524" },
    { label: "Showtime", value: "37" },
    { label: "Starz", value: "43" },
    
    // Turkish Services
    { label: "BluTV", value: "423" },
    { label: "Exxen", value: "581" },
    { label: "BeIN CONNECT", value: "447" },
    
    // Polish Services  
    { label: "Canal+", value: "381" },
    { label: "Player", value: "545" },
    { label: "CDA Premium", value: "467" },
    
    // Portuguese Services
    { label: "RTP Play", value: "596" },
    { label: "MEO", value: "484" },
    { label: "SIC Caras", value: "463" },
    
    // Finnish Services
    { label: "MTV Katsomo", value: "585" },
    { label: "Ruutu", value: "338" },
    { label: "C More", value: "551" },
    
    // Danish Services
    { label: "TV 2 Play", value: "383" },
    { label: "Viaplay", value: "76" },
    { label: "DR TV", value: "541" }
  ];

  const ageOptionsExtended = [
    { label: "Kids (G, TV-Y)", value: "Kids" },
    { label: "Young Adults (PG, PG-13, TV-PG)", value: "Young" },
    { label: "Adults (R, TV-MA)", value: "Adults" }
  ];

  const providerOptionsWithAny = [
    { label: "All Providers", value: "Any" },
    ...providerOptionsExtended,
  ];

  const countrySummaryLabel =
    countryOptionsExtended.find((o) => o.value === region)?.label ?? region;
  const providerSummaryLabel =
    providerOptionsWithAny.find(
      (o) => String(o.value) === String(selectedProvider)
    )?.label ?? "All Providers";
  const ageSummaryLabel =
    ageOptionsExtended.find((o) => o.value === ageCategory)?.label ?? ageCategory;
  const ageSummaryShort = ageSummaryLabel.includes("(")
    ? ageSummaryLabel.split("(")[0].trim()
    : ageSummaryLabel;
  const providerSummaryShort =
    providerSummaryLabel === "All Providers" ? "All" : providerSummaryLabel;
  const settingsSummaryText = `${countrySummaryLabel} · ${providerSummaryShort} · ${ageSummaryShort}`;

  const settingsFields = (
    <div className="settings-grid-compact">
      <div className="setting-group-compact">
        <span className="setting-label-compact">
          <span role="img" aria-label="country">🌍</span> Your Country
        </span>
        <CustomDropdown
          label="Select Country"
          value={region}
          options={countryOptionsExtended}
          onChange={setRegion}
          placeholder="United States"
        />
      </div>

      <div className="setting-group-compact">
        <span className="setting-label-compact">
          <span role="img" aria-label="provider">📺</span> Streaming Provider
        </span>
        <CustomDropdown
          label="Select Streaming Provider"
          value={selectedProvider}
          options={providerOptionsWithAny}
          onChange={setSelectedProvider}
          placeholder="All Providers"
        />
      </div>

      <div className="setting-group-compact">
        <span className="setting-label-compact">
          <span role="img" aria-label="age">👤</span> Your Age
        </span>
        <CustomDropdown
          label="Select Age Category"
          value={ageCategory}
          options={ageOptionsExtended}
          onChange={setAgeCategory}
          placeholder="Select Age Category"
        />
      </div>
    </div>
  );

  return (
    <div className="cinematic-panel-compact mb-3">
      <div className="panel-content-compact">
        
        {useSettingsSheet ? (
          <>
            <div className="settings-toggle filterbar-settings-summary-wrap">
              <button
                type="button"
                className="filterbar-settings-summary-btn"
                onClick={() => setShowSettings(true)}
                aria-haspopup="dialog"
                aria-expanded={showSettings}
                aria-controls="personal-settings-region"
              >
                <span className="filterbar-settings-summary-text">{settingsSummaryText}</span>
                <span className="filterbar-settings-summary-chevron" aria-hidden="true">
                  ▼
                </span>
              </button>
              <p className="filterbar-settings-summary-hint mb-0">Tap to change country, provider, or age</p>
            </div>

            <Modal
              show={showSettings}
              onHide={() => setShowSettings(false)}
              fullscreen="lg-down"
              scrollable
              centered
              className="personal-settings-sheet"
              contentClassName="personal-settings-sheet-content"
            >
              <Modal.Header closeButton>
                <Modal.Title className="fs-6">Personal settings</Modal.Title>
              </Modal.Header>
              <Modal.Body className="pt-2">
                <div id="personal-settings-region" role="region" aria-label="Personal settings">
                  {settingsFields}
                </div>
              </Modal.Body>
            </Modal>
          </>
        ) : (
          <>
            <div className="settings-toggle filterbar-settings-summary-wrap">
              <button
                type="button"
                className="filterbar-settings-summary-btn"
                onClick={() => setShowSettings((s) => !s)}
                aria-expanded={showSettings}
                aria-controls="personal-settings-region"
                id="personal-settings-summary-desktop"
              >
                <span className="filterbar-settings-summary-text">{settingsSummaryText}</span>
                <span className="filterbar-settings-summary-chevron" aria-hidden="true">
                  {showSettings ? "▲" : "▼"}
                </span>
              </button>
              <p className="filterbar-settings-summary-hint mb-0">
                Click to change country, provider, or age
              </p>
            </div>

            <Collapse in={showSettings}>
              <div id="personal-settings-region" className="personal-settings mt-2" role="region" aria-label="Personal settings" aria-labelledby="personal-settings-summary-desktop">
                {settingsFields}
              </div>
            </Collapse>
          </>
        )}

        <div className={`main-controls-compact mt-3 ${useSettingsSheet ? "filterbar-main-controls-mobile" : ""}`}>
          <div className={`control-section-centered ${useSettingsSheet ? "filterbar-control-section-mobile" : ""}`}>
            
            <div className={`control-group-compact ${useSettingsSheet ? "filterbar-control-group-mobile" : ""}`}>
              <span className="control-label-compact">Sort by:</span>
              <div className="badge-group filterbar-pills-mobile-scroll filterbar-sort-pills">
                {(useSettingsSheet
                  ? [
                      { label: "Popular", value: "popularity.desc", color: "trending" },
                      { label: "Top", value: "vote_average.desc", color: "premium" },
                      { label: "Recent", value: "release_date.desc", color: "fresh" },
                    ]
                  : [
                      { label: "Popular", value: "popularity.desc", color: "trending" },
                      { label: "Top Rated", value: "vote_average.desc", color: "premium" },
                      { label: "Recent", value: "release_date.desc", color: "fresh" },
                    ]
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSortOption(opt.value)}
                    className={`color-badge ${opt.color} ${sortOption === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className={`control-group-compact ${useSettingsSheet ? "filterbar-control-group-mobile" : ""}`}>
              <span className="control-label-compact">Content type:</span>
              <div className="badge-group filterbar-pills-mobile-scroll filterbar-type-pills">
                {[
                  { label: "Movies", value: "movie", color: "cinema" },
                  { label: "TV Series", value: "tv", color: "series" }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setVideoType(opt.value)}
                    className={`color-badge ${opt.color} ${videoType === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default FilterBar;
